import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useController } from 'react-hook-form';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import CustomIcon from '@/components/CustomIcon';
import { SettingsComponent } from '../../lib/declarations/EditorComponentTypes';
import { useCurrentBlock } from '../CurrentBlockProvider';
import { BlockSpecificPage } from '../../lib/declarations/BlockSpecificPage';
import ActionMenu from '../../../components/ActionMenu';
import { BaseBlock } from '../../../declarations/models/blocks/BaseBlock';
import { AudioLayout } from '../../../declarations/models/AudioLayout';

const LAYOUT_OPTIONS: Array<AudioLayout> = Object.values(AudioLayout);
const LAYOUT_OPTIONS_ICONS: Record<AudioLayout, ReactNode> = {
  [AudioLayout.EXTENDED]: <CustomIcon name='banner_image_above' />,
  [AudioLayout.SIMPLE]: <MaterialSymbol name='play_arrow' />,
  [AudioLayout.NORMAL]: <CustomIcon name='banner_image_left' />,
};

export const PageBlockAudioLayoutSetting: SettingsComponent = () => {
  const { t: tAria } = useTranslation('aria');
  const { t: tComponents } = useTranslation('components');

  const { blockPath, block } = useCurrentBlock();
  const {
    field: { value, onChange },
  } = useController<BlockSpecificPage<BaseBlock & { layout?: AudioLayout }>, `${typeof blockPath}.layout`>({
    name: `${blockPath}.layout`,
  });

  const {
    field: { value: source, onChange: onChangeSource },
  } = useController<BlockSpecificPage<BaseBlock & { source?: string }>, `${typeof blockPath}.source`>({
    name: `${blockPath}.source`,
  });

  return source !== 'external' ? (
    <ActionMenu
      id='page-block-layout-selector'
      ariaLabel={tAria('components.PageBlockModuleLayoutSetting.ActionMenuLabel')}
      tooltip={tComponents('PageBlockModuleLayoutSetting.Tooltip')}
      tooltipPlacement='top'
      icon={value ? LAYOUT_OPTIONS_ICONS[value as AudioLayout] : LAYOUT_OPTIONS_ICONS.normal}>
      {LAYOUT_OPTIONS.map((layoutOption) => (
        <MenuItem
          key={layoutOption}
          onClick={() => onChange(layoutOption)}
          selected={value === layoutOption}
          aria-label={tComponents(`AudioBlock.layouts.${layoutOption}`)}>
          <ListItemIcon>{LAYOUT_OPTIONS_ICONS[layoutOption]}</ListItemIcon>
          <ListItemText>{tComponents(`AudioBlock.layouts.${layoutOption}`)}</ListItemText>
        </MenuItem>
      ))}
    </ActionMenu>
  ) : null;
};

export default PageBlockAudioLayoutSetting;
